import { useEffect, useRef, useCallback } from 'react';
import logger from 'itrvl-logger';
import { useUser } from 'common/context/UserContext';
import { useApi } from 'common/hooks/api';

const log = logger(__filename);

function loadSdk() {
  const id = 'featurebase-sdk';
  if (document.getElementById(id)) return;
  const s = document.createElement('script');
  s.id = id;
  s.src = 'https://do.featurebase.app/js/sdk.js';
  document.getElementsByTagName('script')[0].parentNode.insertBefore(s, document.getElementsByTagName('script')[0]);
  if (typeof window.Featurebase !== 'function') {
    window.Featurebase = function () {
      (window.Featurebase.q = window.Featurebase.q || []).push(arguments);
    };
  }
}

function Featurebase() {
  const { user } = useUser();
  const Api = useApi();
  const hasBooted = useRef(false);

  const shutdown = useCallback(() => {
    if (hasBooted.current) {
      window.Featurebase('shutdown');
      hasBooted.current = false;
    }
  }, []);

  const bootAnonymous = useCallback(() => {
    window.Featurebase('boot', {
      appId: process.env.REACT_APP_FEATUREBASE_APP_ID,
      theme: 'light',
      language: 'en',
    });
    hasBooted.current = true;
  }, []);

  const bootAuthenticated = useCallback(
    async _userId => {
      try {
        const { data } = await Api.get('/api/Info/featurebaseToken');
        const { featurebaseJwt } = data;

        window.Featurebase(
          'identify',
          {
            organization: process.env.REACT_APP_FEATUREBASE_ORG,
            featurebaseJwt,
          },
          err => {
            if (err) log.warn('[featurebase] identify failed', err);
          },
        );

        window.Featurebase('boot', {
          appId: process.env.REACT_APP_FEATUREBASE_APP_ID,
          featurebaseJwt,
          theme: 'light',
          language: 'en',
        });
        hasBooted.current = true;
      } catch (err) {
        log.warn('[featurebase] JWT fetch failed, falling back to anonymous', err);
        bootAnonymous();
      }
    },
    [Api, bootAnonymous],
  );

  useEffect(() => {
    loadSdk();
  }, []);

  useEffect(() => {
    shutdown();

    if (user?.userId) {
      bootAuthenticated(user.userId);
    } else {
      bootAnonymous();
    }

    return shutdown;
  }, [user?.userId, shutdown, bootAnonymous, bootAuthenticated]);

  return null;
}

export default Featurebase;
